import { Linkedin, Mail, Wrench, Puzzle, LineChart } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Panel, PageHead } from "@/components/Panel";
import CTABand from "@/components/CTABand";

const differentiators = [
  {
    icon: Wrench,
    title: "We implement.",
    text: "Strategy without execution is a document. We stay through deployment and training.",
  },
  {
    icon: Puzzle,
    title: "We're system-agnostic.",
    text: "We recommend the stack that fits your business, not the one we have a partnership with.",
  },
  {
    icon: LineChart,
    title: "We measure.",
    text: "Every engagement defines its success metrics up front and reports against them.",
  },
];

const leaders = [
  {
    name: "Adam Lightfield",
    title: "Chief Executive Officer",
    email: "adam@revgengenius.com",
    linkedin: "https://www.linkedin.com/in/adam-lightfield/",
    photo: "/assets/headshot-adam-circle-transparent.png",
    alt: "Adam Lightfield, Chief Executive Officer, RevGen Genius",
  },
  {
    name: "Jaime Bolaños",
    title: "Head of Operations",
    email: "jaime@revgengenius.com",
    linkedin: "https://www.linkedin.com/in/jaime-bolanos-r/",
    photo: "/assets/headshot-jaime-circle-transparent.png",
    alt: "Jaime Bolaños, Head of Operations, RevGen Genius",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About RevGen Genius — RevOps Consultancy"
        description="RevGen Genius is a results-driven RevOps consultancy uniting sales, marketing, and customer success into a seamless, growth-oriented revenue engine."
        path="/about"
      />
      <div className="mx-auto max-w-[1240px] space-y-24 px-5 sm:px-8">
        <PageHead
          testId="about-head"
          label="About"
          title="About RevGen Genius"
        />

        <Reveal>
          <Panel label="Who We Are" className="pt-16">
            <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
              <div>
                <p className="text-[1.1rem] leading-relaxed text-[#17203A]">
                  RevGen Genius is a results-driven RevOps consultancy specialized in uniting sales,
                  marketing, and customer success functions to create a seamless, growth-oriented
                  revenue engine.
                </p>
                <p className="mt-5 leading-relaxed text-[#5B6784]">
                  We're operators. Every engagement is run by people who have built and fixed revenue
                  systems inside real companies — which is why our recommendations come with
                  implementation attached rather than a handoff.
                </p>
              </div>
              <div className="relative overflow-hidden rounded-[28px]">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=70"
                  alt="Consultants collaborating with a client team around laptops"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="absolute bottom-4 left-4 rounded-2xl bg-[#1F5FFF] px-5 py-3 font-heading text-sm font-semibold text-white shadow-[0_2px_12px_rgba(31,95,255,0.25)]">
                  Operators, not slide decks.
                </div>
              </div>
            </div>
          </Panel>
        </Reveal>

        <Reveal>
          <Panel label="Our Mission" tint className="pt-16">
            <h2 className="h2 max-w-4xl">
              We empower businesses to align teams, streamline processes, and leverage data-driven
              insights for sustainable, scalable revenue growth.
            </h2>
          </Panel>
        </Reveal>

        <Reveal>
          <Panel label="How We're Different" className="pt-16">
            <div className="grid gap-10 md:grid-cols-3">
              {differentiators.map((d, i) => (
                <Reveal key={d.title} delay={i * 0.1}>
                  <div data-testid={`differentiator-${i}`} className="flex gap-4">
                    <d.icon size={28} strokeWidth={1.8} className="mt-1 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                    <div>
                      <h2 className="font-heading text-lg font-semibold">{d.title}</h2>
                      <p className="mt-2 text-[0.95rem] leading-relaxed text-[#5B6784]">{d.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Panel>
        </Reveal>

        <Reveal>
          <Panel label="Leadership" tint className="pt-16">
            <div className="grid gap-6 md:grid-cols-2">
              {leaders.map((l, i) => (
                <Reveal key={l.name} delay={i * 0.1}>
                  <article
                    data-testid={`leader-card-${i}`}
                    className="card-lift flex h-full flex-col rounded-[28px] border-[1.5px] border-[#1F5FFF] bg-white p-8 sm:flex-row sm:items-center sm:gap-7"
                  >
                    <img
                      src={l.photo}
                      alt={l.alt}
                      width={184}
                      height={184}
                      loading="lazy"
                      decoding="async"
                      data-testid={`leader-photo-${i}`}
                      className="h-40 w-40 shrink-0 rounded-full border-[3px] border-[#EAF1FF] object-contain sm:h-[184px] sm:w-[184px]"
                    />
                    <div className="mt-6 sm:mt-0">
                      <h3 className="font-heading text-xl font-semibold">{l.name}</h3>
                      <p className="mt-1 text-sm font-medium text-[#5B6784]">{l.title}</p>
                      <div className="mt-4 space-y-2">
                        <a
                          href={`mailto:${l.email}`}
                          data-testid={`leader-email-${i}`}
                          className="flex items-center gap-2.5 text-[0.92rem] font-medium text-[#17203A] transition-colors hover:text-[#1F5FFF]"
                        >
                          <Mail size={16} className="text-[#1F5FFF]" aria-hidden="true" /> {l.email}
                        </a>
                        <a
                          href={l.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`leader-linkedin-${i}`}
                          aria-label={`${l.name} on LinkedIn`}
                          className="inline-flex items-center gap-2.5 text-[0.92rem] font-medium text-[#17203A] transition-colors hover:text-[#1F5FFF]"
                        >
                          <Linkedin size={16} className="text-[#1F5FFF]" aria-hidden="true" /> LinkedIn
                        </a>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </Panel>
        </Reveal>

        <CTABand
          title="Let's talk about your revenue engine."
          text="Book a free assessment and we'll evaluate where your revenue engine is losing momentum."
          testId="about-cta-book-button"
        />
      </div>
    </div>
  );
}
